/**
 * SVG "avatars" for MVP — stylized, colorful, on-brand.
 * Premium animated Carlos with framer-motion (Lottie-quality feel):
 *   - Blinking eyes on idle
 *   - Breathing chest (subtle scale-Y on body)
 *   - Spring-physics arm wave
 *   - Confetti + rotating stars + bouncing "+1" badge for `correct`
 *   - Falling tear + sweat drop + slumped shoulders for `wrong`
 *   - Floating question mark for `thinking`
 *
 * Moods: "idle" | "wave" | "thinking" | "correct" | "wrong"
 *
 * PHOTO OVERRIDE: when an admin has generated a photorealistic portrait
 * via `/api/admin/portraits/generate` (key: `portrait_<name>`), the SVG
 * is replaced by a rounded photo, keeping the exact same size + label
 * so any layout still works.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";

// -------- Portrait cache (module-level, fetched once per page load) --------
let _portraitCache = null;
let _portraitPending = null;
const loadPortraits = async () => {
  if (_portraitCache) return _portraitCache;
  if (_portraitPending) return _portraitPending;
  _portraitPending = (async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${base}/api/portraits`);
      const list = await res.json();
      const m = {};
      for (const it of list || []) {
        if (typeof it.key === "string" && it.key.startsWith("portrait_")) {
          m[it.key.slice("portrait_".length).toLowerCase()] = it;
        }
      }
      _portraitCache = m;
      return m;
    } catch {
      _portraitCache = {};
      return {};
    }
  })();
  return _portraitPending;
};

const usePortrait = (name) => {
  const [p, setP] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    loadPortraits().then((m) => {
      if (alive) setP(m[name.toLowerCase()] || null);
    });
    return () => {
      alive = false;
    };
  }, [name]);
  return p;
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const PhotoAvatar = ({ portrait, size, label, className = "", ring = "#005BBB" }) => (
  <img
    src={`${API_BASE}/api/illustrations/${portrait.key}`}
    alt={label}
    aria-label={label}
    loading="lazy"
    data-testid={`photo-avatar-${label.toLowerCase()}`}
    className={className}
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      objectFit: "cover",
      border: `3px solid ${ring}`,
      boxShadow: "3px 3px 0 rgba(0,0,0,0.6)",
      display: "inline-block",
    }}
  />
);

// Reusable blinking eye — 2 keyframes give a natural micro-blink.
const Eye = ({ cx, cy, mood }) => {
  const closed = mood === "wrong" ? 0.35 : 1;
  return (
    <motion.g style={{ transformOrigin: `${cx}px ${cy}px` }}>
      <motion.ellipse
        cx={cx} cy={cy}
        rx={3} ry={3 * closed}
        fill="#000"
        animate={mood === "idle" || mood === "wave" ? { ry: [3, 0.3, 3] } : {}}
        transition={{
          duration: 0.25,
          repeat: Infinity,
          repeatDelay: mood === "idle" ? 3 : 2,
          ease: "easeInOut",
        }}
      />
    </motion.g>
  );
};

export const Carlos = ({ size = 96, className = "", waving = false, mood: propMood }) => {
  const portrait = usePortrait("carlos");
  if (portrait && !propMood) {
    // Photo-real override — only use for neutral moods so the animated
    // reactions (correct/wrong/thinking) keep the SVG expressiveness.
    return <PhotoAvatar portrait={portrait} size={size} label="Carlos" className={className} ring="#005BBB" />;
  }
  const mood = propMood || (waving ? "wave" : "idle");

  const body = {
    idle:     { y: [0, -1.5, 0], rotate: 0, transition: { duration: 3, repeat: Infinity, ease: "easeInOut" } },
    wave:     { y: [0, -3, 0], rotate: 0, transition: { duration: 1.3, repeat: Infinity, ease: "easeInOut" } },
    thinking: { y: 0, rotate: [-1.5, 1.5, -1.5], transition: { duration: 3, repeat: Infinity, ease: "easeInOut" } },
    correct:  { y: [0, -14, 0, -8, 0], rotate: 0, transition: { duration: 0.85, repeat: Infinity, repeatDelay: 0.4, type: "spring", stiffness: 220 } },
    wrong:    { y: [0, 2, 0], rotate: [-5, 5, -5, 5, 0], transition: { duration: 0.75, repeat: Infinity, repeatDelay: 1.1 } },
  }[mood];

  const chest = {
    idle:     { scaleY: [1, 1.04, 1] },
    wave:     { scaleY: [1, 1.06, 1] },
    thinking: { scaleY: [1, 1.02, 1] },
    correct:  { scaleY: [1, 1.08, 1] },
    wrong:    { scaleY: [1, 0.98, 1] },
  }[mood];

  const arm = {
    animate:
      mood === "wave" ? { rotate: [0, -28, 22, -28, 22, 0] }
      : mood === "correct" ? { rotate: [0, -35, 0, -35, 0], y: [0, -4, 0, -4, 0] }
      : mood === "thinking" ? { rotate: [-10, -18, -10] }
      : { rotate: 0 },
    transition: {
      duration: mood === "correct" ? 0.9 : 1.4,
      repeat: Infinity,
      ease: "easeInOut",
      type: mood === "correct" ? "spring" : "tween",
    },
  };

  const mouth = {
    idle:     "M54 56c4 5 16 5 20 0",
    wave:     "M54 56c4 5 16 5 20 0",
    thinking: "M56 58h16",
    correct:  "M52 55c5 10 19 10 24 0",
    wrong:    "M54 62c4 -6 16 -6 20 0",
  }[mood];

  // Cheek blush for correct
  const showBlush = mood === "correct";

  return (
    <motion.svg
      viewBox="0 0 128 138" width={size} height={size * (138 / 128)}
      className={className} aria-label="Carlos"
      animate={body} initial={false}
    >
      {/* Ground shadow that stretches when Carlos jumps */}
      <motion.ellipse
        cx="64" cy="128" rx="34" ry="4" fill="#000" fillOpacity="0.15"
        animate={{ rx: mood === "correct" ? [34, 22, 34, 26, 34] : mood === "wave" ? [34, 30, 34] : [34, 33, 34] }}
        transition={{ duration: mood === "correct" ? 0.85 : 1.5, repeat: Infinity }}
      />

      {/* body with breathing */}
      <motion.g style={{ transformOrigin: "64px 118px" }} animate={chest} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}>
        <rect x="28" y="72" width="72" height="46" rx="10" fill="#005BBB" stroke="#000" strokeWidth="3" />
        <rect x="52" y="72" width="24" height="10" fill="#FFD100" stroke="#000" strokeWidth="3" />
      </motion.g>

      {/* head slightly tilts on thinking + wrong */}
      <motion.g
        style={{ transformOrigin: "64px 72px" }}
        animate={{ rotate: mood === "thinking" ? [-6, -3, -6] : mood === "wrong" ? [-2, 2, -2] : 0 }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
      >
        <circle cx="64" cy="46" r="26" fill="#F1C27D" stroke="#000" strokeWidth="3" />
        <path d="M40 40c4-14 44-14 48 0-6-4-14-6-24-6s-18 2-24 6z" fill="#3d2b1f" stroke="#000" strokeWidth="3" />

        {/* Blush */}
        <AnimatePresence>
          {showBlush && (
            <motion.g initial={{ opacity: 0 }} animate={{ opacity: 0.85 }} exit={{ opacity: 0 }}>
              <ellipse cx="49" cy="53" rx="4" ry="2.5" fill="#E3809E" />
              <ellipse cx="79" cy="53" rx="4" ry="2.5" fill="#E3809E" />
            </motion.g>
          )}
        </AnimatePresence>

        <Eye cx={56} cy={46} mood={mood} />
        <Eye cx={72} cy={46} mood={mood} />
        <motion.path
          d={mouth}
          stroke="#000" strokeWidth="3" fill="none" strokeLinecap="round"
          animate={{ d: mouth }} transition={{ duration: 0.25 }}
        />
      </motion.g>

      {/* tie */}
      <path d="M60 78l4 6 4-6-4-4z" fill="#E3000F" stroke="#000" strokeWidth="2" />

      {/* Left arm (static except correct) */}
      {mood === "correct" && (
        <motion.g
          style={{ transformOrigin: "32px 90px" }}
          animate={{ rotate: [0, 35, 0, 35, 0] }}
          transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut", type: "spring", stiffness: 220 }}
        >
          <rect x="24" y="80" width="10" height="26" rx="4" fill="#F1C27D" stroke="#000" strokeWidth="3" />
          {/* thumbs-up hand */}
          <circle cx="29" cy="76" r="6" fill="#F1C27D" stroke="#000" strokeWidth="2" />
          <rect x="27" y="66" width="4" height="10" rx="2" fill="#F1C27D" stroke="#000" strokeWidth="2" />
        </motion.g>
      )}

      {/* Right arm — spring-physics wave */}
      {(mood === "wave" || mood === "correct" || mood === "thinking") && (
        <motion.g style={{ transformOrigin: "96px 90px" }} animate={arm.animate} transition={arm.transition}>
          <rect x="94" y="80" width="10" height="26" rx="4" fill="#F1C27D" stroke="#000" strokeWidth="3" />
          {mood === "thinking" && (
            <circle cx="99" cy="78" r="4" fill="#F1C27D" stroke="#000" strokeWidth="2" />
          )}
        </motion.g>
      )}

      {/* Confetti + rotating stars for correct */}
      {mood === "correct" && (
        <g>
          {[
            [18, 20, "#FFD100", 0],  [110, 22, "#E3000F", 0.1],
            [16, 60, "#005BBB", 0.2], [112, 58, "#FFD100", 0.3],
            [28, 100, "#E3000F", 0.4], [100, 105, "#005BBB", 0.15],
            [8, 40, "#4CAF50", 0.25], [120, 40, "#F97316", 0.35],
          ].map(([x, y, c, delay], i) => (
            <motion.circle
              key={i} cx={x} cy={y} r={3} fill={c}
              animate={{
                opacity: [0, 1, 0],
                scale: [0.3, 1.2, 0.3],
                y: [0, -8, 4, 0],
                rotate: [0, 180, 360],
              }}
              transition={{ duration: 1.1, repeat: Infinity, delay }}
            />
          ))}
          {/* Bouncing +1 badge */}
          <motion.g
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: [0, -10, 0], opacity: [0, 1, 1, 0] }}
            transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 0.3 }}
          >
            <circle cx="108" cy="18" r="10" fill="#FFD100" stroke="#000" strokeWidth="2" />
            <text x="108" y="22" textAnchor="middle" fontSize="10" fontWeight="900" fill="#000">+1</text>
          </motion.g>
        </g>
      )}

      {/* Falling tear + sweat for wrong */}
      {mood === "wrong" && (
        <>
          <motion.path
            d="M50 52 q -2 6 0 10 q 2 -4 0 -10"
            fill="#5CB1E1" stroke="#000" strokeWidth="1"
            animate={{ opacity: [0, 1, 0], y: [0, 18, 22] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
          <motion.path
            d="M78 38 q -2 5 0 8 q 2 -3 0 -8"
            fill="#5CB1E1" stroke="#000" strokeWidth="0.8"
            animate={{ opacity: [0, 1, 0], y: [0, 10, 14] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: 0.4 }}
          />
        </>
      )}

      {/* Floating question mark for thinking */}
      {mood === "thinking" && (
        <motion.g
          animate={{ y: [-2, -8, -2], scale: [1, 1.15, 1], rotate: [-4, 4, -4] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <circle cx="100" cy="24" r="12" fill="#FFD100" stroke="#000" strokeWidth="2" />
          <text x="100" y="30" textAnchor="middle" fontSize="16" fontWeight="900" fill="#000">?</text>
        </motion.g>
      )}
    </motion.svg>
  );
};

export const Ana = ({ size = 96, className = "" }) => {
  const portrait = usePortrait("ana");
  if (portrait) {
    return <PhotoAvatar portrait={portrait} size={size} label="Ana" className={className} ring="#E3000F" />;
  }
  return (
    <svg viewBox="0 0 128 128" width={size} height={size} className={className} aria-label="Ana">
      <rect x="28" y="72" width="72" height="46" rx="10" fill="#E3000F" stroke="#000" strokeWidth="3" />
      <rect x="52" y="72" width="24" height="10" fill="#fff" stroke="#000" strokeWidth="3" />
      <circle cx="64" cy="46" r="26" fill="#F5D6BA" stroke="#000" strokeWidth="3" />
      {/* pony hair */}
      <path d="M36 42c0-18 56-18 56 0-4-2-20-8-28-8s-24 6-28 8z" fill="#5b2c1a" stroke="#000" strokeWidth="3" />
      <path d="M90 46c8 4 10 20 4 30-6-2-10-14-8-24" fill="#5b2c1a" stroke="#000" strokeWidth="3" />
      <circle cx="56" cy="48" r="3" fill="#000" />
      <circle cx="72" cy="48" r="3" fill="#000" />
      <path d="M54 58c4 4 16 4 20 0" stroke="#000" strokeWidth="3" fill="none" strokeLinecap="round" />
      <circle cx="50" cy="56" r="3" fill="#f5a5a5" opacity="0.7" />
      <circle cx="78" cy="56" r="3" fill="#f5a5a5" opacity="0.7" />
    </svg>
  );
};

// Additional photoreal-only team avatars for Lupita, Sofía, Roberto.
// They gracefully render a placeholder ring until an admin generates their portrait.
const NamedPortraitAvatar = ({ name, ring, size, className }) => {
  const portrait = usePortrait(name);
  if (portrait) {
    return <PhotoAvatar portrait={portrait} size={size} label={name} className={className} ring={ring} />;
  }
  return (
    <div
      className={className}
      aria-label={name}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "#FFF6D9",
        border: `3px dashed ${ring}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Fredoka', system-ui",
        fontWeight: 900,
        color: "#0d1a3f",
        fontSize: size * 0.35,
      }}
    >
      {name[0].toUpperCase()}
    </div>
  );
};

export const Lupita = ({ size = 96, className = "" }) => (
  <NamedPortraitAvatar name="lupita" ring="#005BBB" size={size} className={className} />
);
export const Sofia = ({ size = 96, className = "" }) => (
  <NamedPortraitAvatar name="sofia" ring="#005BBB" size={size} className={className} />
);
export const Roberto = ({ size = 96, className = "" }) => (
  <NamedPortraitAvatar name="roberto" ring="#000000" size={size} className={className} />
);

export const Mascot = ({ size = 96, className = "" }) => (
  // Sillón Canales — a smiling blue sofa. Simple and lovable.
  <svg viewBox="0 0 160 128" width={size} height={size} className={className} aria-label="Sillón Canales">
    {/* body */}
    <rect x="12" y="52" width="136" height="52" rx="16" fill="#005BBB" stroke="#000" strokeWidth="3" />
    {/* cushions */}
    <rect x="24" y="60" width="52" height="30" rx="8" fill="#3a86ff" stroke="#000" strokeWidth="3" />
    <rect x="84" y="60" width="52" height="30" rx="8" fill="#3a86ff" stroke="#000" strokeWidth="3" />
    {/* legs */}
    <rect x="18" y="100" width="10" height="14" fill="#000" />
    <rect x="132" y="100" width="10" height="14" fill="#000" />
    {/* arms */}
    <rect x="2" y="46" width="18" height="60" rx="8" fill="#004a99" stroke="#000" strokeWidth="3" />
    <rect x="140" y="46" width="18" height="60" rx="8" fill="#004a99" stroke="#000" strokeWidth="3" />
    {/* face */}
    <circle cx="60" cy="72" r="3" fill="#000" />
    <circle cx="100" cy="72" r="3" fill="#000" />
    <path d="M64 80c4 5 28 5 32 0" stroke="#000" strokeWidth="3" fill="none" strokeLinecap="round" />
    {/* yellow bow */}
    <path d="M76 46l4-8 4 8-4 6z" fill="#FFD100" stroke="#000" strokeWidth="2" />
  </svg>
);

export const GonzalezFamily = ({ size = 220, className = "" }) => (
  <svg viewBox="0 0 320 160" width={size} height={size * 0.5} className={className} aria-label="Familia González">
    {/* Dad */}
    <g transform="translate(20,20)">
      <circle cx="30" cy="30" r="22" fill="#F1C27D" stroke="#000" strokeWidth="3" />
      <rect x="8" y="52" width="44" height="70" rx="8" fill="#005BBB" stroke="#000" strokeWidth="3" />
      <circle cx="24" cy="30" r="2" fill="#000" />
      <circle cx="36" cy="30" r="2" fill="#000" />
      <path d="M22 38c2 3 12 3 14 0" stroke="#000" strokeWidth="2" fill="none" />
    </g>
    {/* Mom */}
    <g transform="translate(90,20)">
      <circle cx="30" cy="30" r="22" fill="#F5D6BA" stroke="#000" strokeWidth="3" />
      <path d="M8 28c0-16 44-16 44 0-4-2-16-6-22-6s-18 4-22 6z" fill="#8B4513" stroke="#000" strokeWidth="3" />
      <rect x="8" y="52" width="44" height="70" rx="8" fill="#E3000F" stroke="#000" strokeWidth="3" />
      <circle cx="24" cy="32" r="2" fill="#000" />
      <circle cx="36" cy="32" r="2" fill="#000" />
      <path d="M22 40c2 3 12 3 14 0" stroke="#000" strokeWidth="2" fill="none" />
    </g>
    {/* Kid 1 */}
    <g transform="translate(170,50)">
      <circle cx="24" cy="24" r="18" fill="#F1C27D" stroke="#000" strokeWidth="3" />
      <rect x="6" y="42" width="36" height="52" rx="6" fill="#FFD100" stroke="#000" strokeWidth="3" />
      <circle cx="18" cy="24" r="2" fill="#000" />
      <circle cx="30" cy="24" r="2" fill="#000" />
      <path d="M18 32c2 2 8 2 10 0" stroke="#000" strokeWidth="2" fill="none" />
    </g>
    {/* Kid 2 */}
    <g transform="translate(230,55)">
      <circle cx="22" cy="22" r="16" fill="#F5D6BA" stroke="#000" strokeWidth="3" />
      <rect x="6" y="38" width="32" height="48" rx="6" fill="#4CAF50" stroke="#000" strokeWidth="3" />
      <circle cx="17" cy="22" r="2" fill="#000" />
      <circle cx="27" cy="22" r="2" fill="#000" />
      <path d="M17 28c2 2 6 2 10 0" stroke="#000" strokeWidth="2" fill="none" />
    </g>
  </svg>
);

export const CharacterBadge = ({ character = "carlos", label = "" }) => {
  const Comp = character === "carlos" ? Carlos : character === "ana" ? Ana : Mascot;
  return (
    <div className="flex items-center gap-3">
      <Comp size={56} />
      {label && <span className="font-display font-bold text-lg">{label}</span>}
    </div>
  );
};
